<template>
  <b-col
    md="12"
    class="mt-3 mb-2"
  >
    <div
      v-if="loading"
      class="suggestions-card"
    >
      <div class="suggestions-header">
        <b-spinner
          small
          variant="warning"
          class="mr-2"
        />
        <span class="font-weight-bold">{{ $t('Carregando sugestões...') }}</span>
      </div>
    </div>
    <div
      v-else-if="suggestions.length > 0"
      class="suggestions-card"
    >
      <div class="suggestions-header">
        <span class="font-weight-bold">{{ $t('Que tal pedir também?') }}</span>
      </div>

      <!-- Layout Horizontal com scroll -->
      <div
        v-if="orientation === 'horizontal'"
        class="suggestions-list-horizontal"
      >
        <ul class="list-unstyled suggestions-scroll-container">
          <atm-cart-item
            v-for="suggestion in suggestions"
            :key="suggestion.skuId"
            :cart-item="transformSuggestionToCartItem(suggestion)"
            :is-suggestion="true"
            :orientation="orientation == 'horizontal' ? 'vertical' : 'horizontal'"
            :busy="busy"
            @add-suggestion="onAddSuggestion"
          />
        </ul>
      </div>

      <!-- Layout Vertical sem scroll -->
      <div
        v-else
        class="suggestions-list"
      >
        <ul class="list-unstyled">
          <atm-cart-item
            v-for="suggestion in suggestions"
            :key="suggestion.skuId"
            :cart-item="transformSuggestionToCartItem(suggestion)"
            :is-suggestion="true"
            :orientation="orientation == 'horizontal' ? 'vertical' : 'horizontal'"
            :busy="busy"
            @add-suggestion="onAddSuggestion"
          />
        </ul>
      </div>
    </div>
  </b-col>
</template>

<script>
import { BCol, BSpinner } from 'bootstrap-vue'
import { mapGetters, mapState, mapActions } from 'vuex'
import { payBoxUtils } from '@/mixins'
import AtmCartItem from './cards/AtmCartItem.vue'

export default {
  components: {
    BCol,
    BSpinner,
    AtmCartItem,
  },

  props: {
    cartItems: {
      type: Array,
      default: () => [],
    },
    sidebarOpen: {
      type: Boolean,
      default: false,
    },
    orientation: {
      type: String,
      default: 'vertical',
      validator: value => ['horizontal', 'vertical'].includes(value),
    },
    busy: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {}
  },

  computed: {
    ...mapGetters('pages/pdv/payBoxConfiguration', ['currentPayboxConfiguration']),
    ...mapGetters('pages/pdv/payBox', ['getSuggestions', 'isSuggestionsLoading']),
    ...mapState('pages/pdv/payBox', {
      clientForm: 'clientForm',
    }),
    suggestions() {
      return this.getSuggestions
    },
    loading() {
      return this.isSuggestionsLoading
    },
  },

  watch: {
    sidebarOpen(isOpen) {
      if (isOpen) {
        this.handleLoadSuggestions()
      }
    },
  },

  mounted() {
    // Carrega sugestões iniciais se o sidebar já estiver aberto
    if (this.sidebarOpen) {
      this.handleLoadSuggestions()
    }
  },

  methods: {
    ...mapActions('pages/pdv/payBox', ['loadSuggestions']),
    async handleLoadSuggestions() {
      await this.loadSuggestions(this.cartItems)
    },

    /**
     * Converte dados de sugestão para estrutura compatível com AtmCartItem
     * @param {object} suggestion - PayBoxSkuResponseDto com dados de sugestão
     * @returns {object} - Estrutura compatível com cartItem
     */
    transformSuggestionToCartItem(suggestion) {
      // Usar o método existente do payBoxUtils para construir as regras de preço
      // (detecta automaticamente promoção, preços escalonados, etc)
      const { priceRules } = payBoxUtils.buildPriceRulesFromProduct(suggestion)

      // Selecionar a regra de preço apropriada
      // quantidade 0 para exibição, sem priceTableId, sem productKitId
      const { priceRuleSelected } = payBoxUtils.getPriceRule(
        {
          quantity: 1,
          promotionId: suggestion.promotionId,
          priceTableId: suggestion.priceTable?.id,
          productKitId: null,
        },
        priceRules
      )

      return {
        ...suggestion,
        id: suggestion.skuId,
        localId: suggestion.skuId,
        quantity: 0, // Sempre 0 para sugestões na exibição
        skuProduct: {
          id: suggestion.skuId,
          skuId: suggestion.skuId,
          name: suggestion.name,
          ean: suggestion.ean,
          imageSquare: suggestion.imageSquare,
          kit: false,
          stock: suggestion.quantityStock || suggestion.stock || 0,
        },
        priceRuleSelected,
        priceRules,
        kitItems: [],
        isKit: false,
      }
    },

    async onAddSuggestion(suggestion) {
      try {
        // Emitir evento para o componente pai adicionar o item ao carrinho
        // A ação do Vuex vai buscar o produto completo e aplicar as regras de preço finais
        this.$emit('add-suggestion', {
          skuId: suggestion.skuId,
          ean: suggestion.ean,
          quantity: suggestion.quantity ?? 1,
          priceTableId: suggestion.priceTableId,
          unitValue: suggestion.price || suggestion.unitValue,
          inputMethod: suggestion.inputMethod,
        })
      } catch (error) {
        console.error('Erro ao adicionar sugestão:', error)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.suggestions-card {
  padding: 12px;
  box-shadow: 0 2px 8px rgba(40, 167, 69, 0.15);
  border-radius: 10px;

  .suggestions-header {
    display: flex;
    align-items: center;
    padding: 6px;
    margin-bottom: 10px;
    color: #333;
    font-size: large;
    border-left: 10px solid #28a745;
    border-radius: 6px;
  }

  .suggestions-list-horizontal {
    .suggestions-scroll-container {
      display: flex;
      gap: 16px;
      overflow-x: auto;
      padding: 8px 0;
      scroll-behavior: smooth;

      &::-webkit-scrollbar {
        height: 8px;
      }

      &::-webkit-scrollbar-track {
        background: #f1f1f1;
        border-radius: 10px;
      }

      &::-webkit-scrollbar-thumb {
        background: #28a745;
        border-radius: 4px;

        &:hover {
          background: #1e7c34;
        }
      }

      // Padding para melhor UX no scroll
      li {
        flex-shrink: 0;

        &:first-child {
          margin-left: 8px;
        }

        &:last-child {
          margin-right: 8px;
        }
      }
    }
  }

  .suggestions-list {
    &::-webkit-scrollbar {
      width: 8px;
    }

    &::-webkit-scrollbar-track {
      background: #f1f1f1;
    }

    &::-webkit-scrollbar-thumb {
      background: #28a745;
      border-radius: 4px;

      &:hover {
        background: #28a745;
      }
    }

    ul {
      & > li + li {
        margin-top: 20px;
      }
    }
  }
}
</style>
